import { clientEnv } from "../env";
import type { AiChatRequest, AiChatResponse, AiCoreStatus } from "./ai.types";

interface ApiErrorPayload {
  readonly error?: string;
  readonly message?: string;
}

interface LegacyAiResult {
  readonly requestId?: string;
  readonly status?: string;
  readonly text?: string;
  readonly provider?: string;
  readonly model?: string;
  readonly intent?: {
    readonly primary?: string;
    readonly secondary?: unknown;
    readonly confidence?: number;
  };
  readonly metrics?: {
    readonly provider?: string;
    readonly model?: string;
  };
  readonly usage?: {
    readonly inputTokens?: number;
    readonly outputTokens?: number;
  };
}

export class AiClientError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AiClientError";
    this.status = status;
    this.code = code;
  }
}

export async function sendAiChat(request: AiChatRequest): Promise<AiChatResponse> {
  const lastUserMessage =
    [...request.messages].reverse().find((message) => message.role === "user") ??
    request.messages[request.messages.length - 1];

  const message = lastUserMessage?.content?.trim();

  if (!message) {
    throw new AiClientError(400, "AI_EMPTY_MESSAGE", "El mensaje está vacío.");
  }

  const response = await fetch(`${clientEnv.apiBaseUrl}/ai/chat`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(request.sessionToken ? { Authorization: `Bearer ${request.sessionToken}` } : {})
    },
    body: JSON.stringify({
      message,
      messages: request.messages,
      organizationId: request.organizationId,
      metadata: {
        ...request.metadata,
        moduleId: request.moduleId
      },
      confirmCredits: true
    })
  });

  const payload = await readJson<LegacyAiResult | ApiErrorPayload>(response);

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AiClientError(
      response.status,
      errorPayload.error ?? "AI_REQUEST_FAILED",
      errorPayload.message ?? "No se pudo conectar con el motor IA."
    );
  }

  return normalizeLegacyAiResponse(payload as LegacyAiResult, request);
}

export async function getAiCoreStatus(): Promise<AiCoreStatus> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ai/health`, {
    credentials: "include"
  });

  if (!response.ok) {
    throw new AiClientError(response.status, "AI_STATUS_FAILED", "No se pudo leer el estado del motor IA.");
  }

  const payload = await readJson<Record<string, unknown>>(response);

  return {
    status: "ready",
    version: "local-bridge",
    gateway: clientEnv.apiBaseUrl,
    pipeline: ["frontend", "backend", "omnisynia-ai-core", "openai"],
    provider: "openai",
    model: String(payload.model ?? "gpt-4.1-mini"),
    providerAdapters: [],
    memory: "local consent bridge",
    tools: [],
    mediaEngines: ["socialcut-pro"],
    routes: ["socialcut-pro"],
    authentication: "local development",
    session: "anonymous"
  };
}

function normalizeLegacyAiResponse(result: LegacyAiResult, request: AiChatRequest): AiChatResponse {
  const moduleId = request.moduleId ?? "socialcut-pro";
  const provider = result.provider ?? result.metrics?.provider ?? "openai";
  const model = result.model ?? result.metrics?.model ?? "gpt-4.1-mini";
  const content = result.text ?? "OMNISYNIA no devolvió texto.";

  return {
    id: result.requestId ?? crypto.randomUUID(),
    provider,
    model,
    route: {
      moduleId,
      capability: "chat",
      reason: "Adaptado desde el AI Core local conectado a OpenAI."
    },
    message: {
      role: "assistant",
      content
    },
    safety: {
      memoryAllowed: request.userConsent.memoryOptIn,
      blocked: false
    },
    session: {
      id: request.sessionId ?? "local-session",
      userMode: "anonymous"
    },
    usage: result.usage,
    core: {
      version: "local-bridge",
      pipeline: ["intent", "context", "model-router", "openai", "response"],
      intent: {
        category: "video",
        targetCapability: "socialcut-pro",
        confidence: result.intent?.confidence ?? 1,
        signals: ["frontend-completo", "backend-8787", "openai-activo"],
        requestedModuleId: moduleId
      },
      context: {
        metadata: request.metadata,
        session: {
          sessionId: request.sessionId ?? "local-session",
          userMode: "anonymous"
        },
        requestedModuleId: moduleId,
        messageWindow: {
          count: request.messages.length,
          hasSystemMessage: request.messages.some((message) => message.role === "system")
        },
        constraints: [],
        safety: {
          memoryAllowed: request.userConsent.memoryOptIn,
          blocked: false
        }
      },
      model: {
        provider: provider === "openai" ? "openai" : "omnisynia-local",
        model,
        adapter: provider,
        reason: "Proveedor detectado desde el backend conectado."
      },
      tools: {
        tools: [],
        mediaEngines: [
          {
            id: "socialcut-pro",
            status: "active",
            moduleId,
            reason: "Módulo activo en la interfaz OMNISYNIA."
          }
        ],
        modules: [moduleId]
      },
      memory: {
        mode: request.userConsent.memoryOptIn ? "consent-eligible" : "ephemeral",
        persistence: "none",
        writable: false,
        reason: request.userConsent.memoryOptIn
          ? "Consentimiento recibido; persistencia pendiente de backend."
          : "Memoria desactivada para esta interacción."
      },
      verification: {
        status: "passed",
        checks: ["backend-response-ok", "frontend-normalized"],
        warnings: []
      }
    },
    createdAt: new Date().toISOString()
  };
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new AiClientError(response.status, "AI_INVALID_RESPONSE", "Respuesta IA no válida.");
  }
}
